import { supabase } from "./supabase";

const API = process.env.REACT_APP_API_URL || "";

export function getWsUrl() {
  if (API) return `${API.replace(/^http/, "ws")}/ws`;
  // Relative WebSocket URLs ("/ws") are only legal in browsers since ~2024 —
  // older ones throw a synchronous SyntaxError that crashes the React tree.
  // Build the absolute URL from the page origin instead.
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/ws`;
}

export function getWsToken() {
  return getToken();
}

function getToken() {
  return localStorage.getItem("token");
}

function authHeaders() {
  const token = getToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

function authHeadersNoBody() {
  const token = getToken();
  const headers = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

async function handleResponse(res) {
  if (res.status === 401) {
    // Try refreshing the session before logging out — but only ONCE per page
    // load: if the backend keeps rejecting a token Supabase happily refreshes
    // (deleted profile, secret drift), an unguarded reload would loop forever.
    const alreadyRetried = sessionStorage.getItem("authRetried") === "1";
    const refreshed = !alreadyRetried && (await refreshSession());
    if (refreshed) {
      sessionStorage.setItem("authRetried", "1");
      // Token was refreshed — reload to retry all requests with new token
      window.location.reload();
      throw new Error("Token erneuert, Seite wird neu geladen");
    }
    // Refresh failed (or already retried once) — actually log out
    sessionStorage.removeItem("authRetried");
    await supabase.auth.signOut();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.reload();
    throw new Error("Sitzung abgelaufen");
  }
  // Any successful response proves the token works again — reset the guard.
  if (res.ok) sessionStorage.removeItem("authRetried");
  if (res.status === 403) {
    // Account archived server-side → log the user out immediately.
    let detail = "";
    try { detail = (await res.clone().json()).detail || ""; } catch {}
    if (detail === "Zugang deaktiviert") {
      await supabase.auth.signOut();
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.reload();
      throw new Error("Zugang deaktiviert");
    }
  }
  if (res.status === 409) {
    let msg = "Dieser Eintrag wurde zwischenzeitlich von jemand anderem geändert. Daten werden neu geladen.";
    try {
      const err = await res.json();
      msg = err.detail || msg;
    } catch {}
    const error = new Error(msg);
    error.status = 409;
    throw error;
  }
  if (!res.ok) {
    let msg = "Fehler";
    try {
      const err = await res.json();
      msg = err.detail || msg;
    } catch {}
    throw new Error(msg);
  }
  if (res.status === 204) return null;
  return res.json();
}

// --- Auth (Supabase) ---

export function initAuthListener() {
  supabase.auth.onAuthStateChange((_event, session) => {
    if (session) {
      localStorage.setItem("token", session.access_token);
    } else {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    }
  });

  // Refresh token when user switches back to this tab
  // (browser pauses timers in background tabs, so autoRefreshToken may miss the refresh)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      supabase.auth.getSession().then(({ data }) => {
        if (data?.session) {
          localStorage.setItem("token", data.session.access_token);
        }
      });
    }
  });
}

function usernameToEmail(username) {
  let name = username.toLowerCase().trim();
  const replacements = { "ä": "ae", "ö": "oe", "ü": "ue", "ß": "ss" };
  for (const [old, rep] of Object.entries(replacements)) {
    name = name.replaceAll(old, rep);
  }
  name = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  name = name.replace(/[^a-z0-9]+/g, ".").replace(/^\.+|\.+$/g, "");
  return `${name}@relticc.local`;
}

export async function login(username, password) {
  const email = usernameToEmail(username);
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw new Error(error.message === "Invalid login credentials" ? "Ungültige Anmeldedaten" : error.message);

  localStorage.setItem("token", data.session.access_token);

  // Fetch user profile from backend to get role + username. The id must be
  // persisted too — it is how a non-admin's own records (e.g. vacations) are
  // recognized client-side.
  const me = await fetchMe();
  const stored = { id: me.id, username: me.username, role: me.role };
  localStorage.setItem("user", JSON.stringify(stored));
  // Phasen nur im Speicher (localStorage klein halten): der Kalender braucht
  // sie, um beim eigenen Urlaub die Werkstudenten-Regel anzuwenden.
  return { ...stored, phases: me.phases || [] };
}

export async function logout() {
  await supabase.auth.signOut();
  localStorage.removeItem("token");
  localStorage.removeItem("user");
}

/** Re-read the profile from the server and refresh the cached copy.
 *
 * The cache is only written at login, so without this a name that was wrong when
 * the session started stays wrong for its whole lifetime — a hard reload does not
 * clear localStorage. */
export async function refreshStoredUser() {
  const me = await fetchMe();
  const stored = { id: me.id, username: me.username, role: me.role };
  localStorage.setItem("user", JSON.stringify(stored));
  // Phasen bleiben in-memory (siehe login) — sie sind Anzeige-Kontext, kein
  // Auth-Zustand, und würden den localStorage-Eintrag nur aufblähen.
  return { ...stored, phases: me.phases || [] };
}

export function getStoredUser() {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export async function fetchMe() {
  const res = await fetch(`${API}/api/auth/me`, { headers: authHeadersNoBody() });
  return handleResponse(res);
}

export async function refreshSession() {
  const { data, error } = await supabase.auth.refreshSession();
  if (data?.session) {
    localStorage.setItem("token", data.session.access_token);
    return true;
  }
  if (error) {
    console.warn("Session refresh failed:", error.message);
  }
  return false;
}

// --- Areas ---

export async function fetchAreas() {
  const res = await fetch(`${API}/api/areas`, { headers: authHeadersNoBody() });
  return handleResponse(res);
}

export async function createArea(area) {
  const res = await fetch(`${API}/api/areas`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(area),
  });
  return handleResponse(res);
}

export async function updateArea(key, area) {
  const res = await fetch(`${API}/api/areas/${key}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(area),
  });
  return handleResponse(res);
}

export async function deleteArea(key) {
  const res = await fetch(`${API}/api/areas/${key}`, {
    method: "DELETE",
    headers: authHeadersNoBody(),
  });
  return handleResponse(res);
}

// --- Tasks ---

export async function fetchTasks(fromDate, toDate) {
  const params = new URLSearchParams();
  if (fromDate) params.set("from", fromDate);
  if (toDate) params.set("to", toDate);
  const qs = params.toString() ? `?${params}` : "";
  const res = await fetch(`${API}/api/tasks${qs}`, { headers: authHeadersNoBody() });
  return handleResponse(res);
}

export async function fetchCategories(area) {
  const params = area ? `?area=${encodeURIComponent(area)}` : "";
  const res = await fetch(`${API}/api/tasks/categories${params}`, { headers: authHeadersNoBody() });
  return handleResponse(res);
}

export async function createTask(task) {
  const res = await fetch(`${API}/api/tasks`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(task),
  });
  return handleResponse(res);
}

export async function updateTask(id, task) {
  const res = await fetch(`${API}/api/tasks/${id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(task),
  });
  return handleResponse(res);
}

export async function deleteTask(id) {
  const res = await fetch(`${API}/api/tasks/${id}`, {
    method: "DELETE",
    headers: authHeadersNoBody(),
  });
  return handleResponse(res);
}

export async function moveTask(id, calendar_date, version) {
  const res = await fetch(`${API}/api/tasks/${id}/move`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify({ calendar_date, version }),
  });
  return handleResponse(res);
}

export async function toggleTask(id, version) {
  const res = await fetch(`${API}/api/tasks/${id}/toggle`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify({ version }),
  });
  return handleResponse(res);
}

export async function reorderTasks(items) {
  const res = await fetch(`${API}/api/tasks/reorder`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify(items),
  });
  return handleResponse(res);
}

// --- Holidays ---

export async function fetchHolidays(year) {
  const res = await fetch(`${API}/api/holidays/${year}`, { headers: authHeadersNoBody() });
  return handleResponse(res);
}

// --- Appointments ---

export async function fetchAppointments(fromDate, toDate) {
  const params = new URLSearchParams();
  if (fromDate) params.set("from", fromDate);
  if (toDate) params.set("to", toDate);
  const qs = params.toString() ? `?${params}` : "";
  const res = await fetch(`${API}/api/appointments${qs}`, { headers: authHeadersNoBody() });
  return handleResponse(res);
}

export async function createAppointment(appointment) {
  const res = await fetch(`${API}/api/appointments`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(appointment),
  });
  return handleResponse(res);
}

export async function updateAppointment(id, data) {
  const res = await fetch(`${API}/api/appointments/${id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function deleteAppointment(id) {
  const res = await fetch(`${API}/api/appointments/${id}`, {
    method: "DELETE",
    headers: authHeadersNoBody(),
  });
  return handleResponse(res);
}

// --- Users (admin) ---

export async function fetchUsers() {
  const res = await fetch(`${API}/api/users`, { headers: authHeadersNoBody() });
  return handleResponse(res);
}

export async function createUser(username, password, role, color, abbreviation, email) {
  const res = await fetch(`${API}/api/users`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ username, password, role, color, abbreviation, email }),
  });
  return handleResponse(res);
}

export async function updateUser(id, data) {
  const res = await fetch(`${API}/api/users/${id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function deleteUser(id) {
  const res = await fetch(`${API}/api/users/${id}`, {
    method: "DELETE",
    headers: authHeadersNoBody(),
  });
  return handleResponse(res);
}

// --- Employment phases (return the updated user with phases) ---
export async function createPhase(userId, data) {
  const res = await fetch(`${API}/api/users/${userId}/phases`, {
    method: "POST", headers: authHeaders(), body: JSON.stringify(data),
  });
  return handleResponse(res);
}
export async function updatePhase(phaseId, data) {
  const res = await fetch(`${API}/api/phases/${phaseId}`, {
    method: "PUT", headers: authHeaders(), body: JSON.stringify(data),
  });
  return handleResponse(res);
}
export async function deletePhase(phaseId) {
  const res = await fetch(`${API}/api/phases/${phaseId}`, {
    method: "DELETE", headers: authHeadersNoBody(),
  });
  return handleResponse(res);
}

// --- Employees ---

export async function fetchEmployees(includeArchived = false) {
  const qs = includeArchived ? "?include_archived=true" : "";
  const res = await fetch(`${API}/api/employees${qs}`, { headers: authHeadersNoBody() });
  return handleResponse(res);
}

// --- Outlook / Microsoft Calendar ---

export async function syncAllOutlook() {
  const res = await fetch(`${API}/api/outlook/sync-all`, {
    method: "POST",
    headers: authHeadersNoBody(),
  });
  return handleResponse(res);
}

// --- Settings ---

export async function fetchSetting(key) {
  const res = await fetch(`${API}/api/settings/${key}`, { headers: authHeadersNoBody() });
  return handleResponse(res);
}

export async function saveSetting(key, value) {
  const res = await fetch(`${API}/api/settings/${key}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify({ value }),
  });
  return handleResponse(res);
}

// --- Roadmap ---

export async function fetchRoadmapItems() {
  const res = await fetch(`${API}/api/roadmap`, { headers: authHeadersNoBody() });
  return handleResponse(res);
}

export async function createRoadmapItem(item) {
  const res = await fetch(`${API}/api/roadmap`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(item),
  });
  return handleResponse(res);
}

export async function updateRoadmapItem(id, data) {
  const res = await fetch(`${API}/api/roadmap/${id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function deleteRoadmapItem(id) {
  const res = await fetch(`${API}/api/roadmap/${id}`, {
    method: "DELETE",
    headers: authHeadersNoBody(),
  });
  return handleResponse(res);
}

// --- Workforce: Projects ---

export async function fetchProjects(includeArchived = true) {
  const qs = includeArchived ? "" : "?include_archived=false";
  const res = await fetch(`${API}/api/workforce/projects${qs}`, { headers: authHeadersNoBody() });
  return handleResponse(res);
}

// --- Workforce: Clients ---

export async function fetchClients() {
  const res = await fetch(`${API}/api/workforce/clients`, { headers: authHeadersNoBody() });
  return handleResponse(res);
}

export async function createClient(client) {
  const res = await fetch(`${API}/api/workforce/clients`, {
    method: "POST", headers: authHeaders(), body: JSON.stringify(client),
  });
  return handleResponse(res);
}

export async function updateClient(id, data) {
  const res = await fetch(`${API}/api/workforce/clients/${id}`, {
    method: "PUT", headers: authHeaders(), body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function deleteClient(id) {
  const res = await fetch(`${API}/api/workforce/clients/${id}`, {
    method: "DELETE", headers: authHeadersNoBody(),
  });
  return handleResponse(res);
}

export async function createProject(project) {
  const res = await fetch(`${API}/api/workforce/projects`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(project),
  });
  return handleResponse(res);
}

export async function updateProject(id, data) {
  const res = await fetch(`${API}/api/workforce/projects/${id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function deleteProject(id) {
  const res = await fetch(`${API}/api/workforce/projects/${id}`, {
    method: "DELETE",
    headers: authHeadersNoBody(),
  });
  return handleResponse(res);
}

// --- Workforce: Time entries ---

export async function fetchTimeEntries(userId) {
  const qs = userId != null && userId !== "" && userId !== "all" ? `?user_id=${userId}` : "";
  const res = await fetch(`${API}/api/workforce/entries${qs}`, { headers: authHeadersNoBody() });
  return handleResponse(res);
}

export async function createTimeEntry(entry) {
  const res = await fetch(`${API}/api/workforce/entries`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(entry),
  });
  return handleResponse(res);
}

export async function updateTimeEntry(id, data) {
  const res = await fetch(`${API}/api/workforce/entries/${id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function deleteTimeEntry(id) {
  const res = await fetch(`${API}/api/workforce/entries/${id}`, {
    method: "DELETE",
    headers: authHeadersNoBody(),
  });
  return handleResponse(res);
}

export async function fetchRunningTimer() {
  const res = await fetch(`${API}/api/workforce/timer`, { headers: authHeadersNoBody() });
  return handleResponse(res);
}

export async function startTimer(description) {
  // Projekte werden nachträglich per toggleTimerProject auf den laufenden
  // Timer gebucht — das alte project_id-Feld war ein toter Pfad.
  const res = await fetch(`${API}/api/workforce/timer/start`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ description: description || "" }),
  });
  return handleResponse(res);
}

export async function stopTimer() {
  const res = await fetch(`${API}/api/workforce/timer/stop`, {
    method: "POST",
    headers: authHeadersNoBody(),
  });
  return handleResponse(res);
}

export async function toggleTimerProject(projectId) {
  const res = await fetch(`${API}/api/workforce/timer/project/${projectId}`, {
    method: "POST",
    headers: authHeadersNoBody(),
  });
  return handleResponse(res);
}

export async function setEntryProjectTimes(entryId, projectTimes) {
  const res = await fetch(`${API}/api/workforce/entries/${entryId}/project_times`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify({ project_times: projectTimes }),
  });
  return handleResponse(res);
}

// --- Workforce: Vacation requests ---

// The server broadcasts "workforce" over the WebSocket after every change, but
// that never reaches the tab in dev (the dev server occupies /ws). So mutations
// additionally fire a local event — App listens to it and refreshes the pending
// badge right away, no reload needed.
function notifyWorkforceChanged() {
  try { window.dispatchEvent(new CustomEvent("workforce-changed")); } catch {}
}

export async function fetchVacations(userId) {
  const qs = userId != null && userId !== "" && userId !== "all" ? `?user_id=${userId}` : "";
  const res = await fetch(`${API}/api/workforce/vacations${qs}`, { headers: authHeadersNoBody() });
  return handleResponse(res);
}

export async function createVacation(start_date, end_date, opts = {}) {
  const body = { start_date, end_date };
  if (opts.user_id != null) body.user_id = opts.user_id;
  if (opts.status) body.status = opts.status;
  if (opts.half_day != null) body.half_day = opts.half_day;
  if (opts.half_end != null) body.half_end = opts.half_end;
  const res = await fetch(`${API}/api/workforce/vacations`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  const out = await handleResponse(res);
  notifyWorkforceChanged();
  return out;
}

export async function updateVacation(id, data) {
  const res = await fetch(`${API}/api/workforce/vacations/${id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  const out = await handleResponse(res);
  notifyWorkforceChanged();
  return out;
}

export async function deleteVacation(id) {
  const res = await fetch(`${API}/api/workforce/vacations/${id}`, {
    method: "DELETE",
    headers: authHeadersNoBody(),
  });
  const out = await handleResponse(res);
  notifyWorkforceChanged();
  return out;
}

// --- Rechner (Wake-on-LAN/Ausschalten über den Büro-Dienst) ---

export async function fetchComputers() {
  const res = await fetch(`${API}/api/computers`, { headers: authHeadersNoBody() });
  return handleResponse(res);
}

// { seen_at } — wann sich der Büro-Dienst zuletzt gemeldet hat (null = nie)
export async function fetchComputerAgent() {
  const res = await fetch(`${API}/api/computers/agent`, { headers: authHeadersNoBody() });
  return handleResponse(res);
}

export async function wakeComputer(id) {
  const res = await fetch(`${API}/api/computers/${id}/wake`, {
    method: "POST",
    headers: authHeadersNoBody(),
  });
  return handleResponse(res);
}

export async function shutdownComputer(id, force) {
  const res = await fetch(`${API}/api/computers/${id}/shutdown`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ force: !!force }),
  });
  return handleResponse(res);
}

export async function setComputerOwner(id, ownerUserId) {
  const res = await fetch(`${API}/api/computers/${id}`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify({ owner_user_id: ownerUserId ?? null }),
  });
  return handleResponse(res);
}
